import {
  AlertTriangle,
  GitCompareArrows,
  GitPullRequest,
  Info,
  PlayCircle,
  RefreshCw,
} from "lucide-react";
import { type FormEvent, useCallback, useEffect, useId, useMemo, useState } from "react";
import { ApiError, api } from "../../api/client.js";
import type {
  CheckRunConclusion,
  CheckRunProjection,
  ComparisonOutcome,
  ComparisonResult,
  ScanView,
} from "../../api/types.js";
import { EmptyState, InlineError, LoadingTable } from "../../components/AsyncState.js";
import { Badge } from "../../components/Badge.js";
import { useApiResource } from "../../hooks/useApiResource.js";
import { formatDateTime, shortHash, titleCase } from "../../lib/format.js";
import {
  countDeliveryStatuses,
  deliveryHeadline,
  deliveryStatusTone,
} from "./delivery-projection.js";
import "./delivery-queue.css";

const CHECK_RUN_TONE: Record<CheckRunConclusion, "positive" | "critical" | "warning" | "neutral"> =
  {
    success: "positive",
    failure: "critical",
    action_required: "warning",
    neutral: "neutral",
  };

const COMPARISON_TONE: Record<
  ComparisonOutcome,
  "info" | "neutral" | "positive" | "critical" | "muted"
> = {
  new: "info",
  persistent: "neutral",
  fixed: "positive",
  reopened: "critical",
  not_comparable: "muted",
};

const DELIVERY_EXAMPLES = [
  {
    title: "PR opens with critical findings",
    event: "pull_request.opened",
    outcome: "Projected check fails with the exact replay-backed blockers attached.",
    evidence: "New risk is visible before merge.",
  },
  {
    title: "Fix commit reduces risk",
    event: "push",
    outcome: "Comparison separates fixed findings from persistent ones.",
    evidence: "Reviewers see what improved without losing history.",
  },
  {
    title: "Replay mismatch blocks trust",
    event: "check_suite.rerequested",
    outcome: "A stale citation becomes action-required instead of green.",
    evidence: "The product refuses to rubber-stamp drifted evidence.",
  },
  {
    title: "Coverage gap enters the gate",
    event: "pull_request.synchronize",
    outcome: "Unsupported or unreviewed cells keep the check honest.",
    evidence: "Empty queues do not masquerade as clean scans.",
  },
  {
    title: "Read-only customer PoC",
    event: "workflow_dispatch.demo",
    outcome:
      "Attributable delivery evidence demonstrates the workflow without repository writeback.",
    evidence: "Safe demo behavior stays inside the hosted-PoC boundary.",
  },
];

function ScanOption({ scan }: { scan: ScanView }) {
  return (
    <option value={scan.id}>
      {scan.repositoryName} — {shortHash(scan.id, 10)} ({titleCase(scan.status)})
    </option>
  );
}

function DeliveryExamples() {
  return (
    <section className="delivery-examples" aria-labelledby="delivery-examples-heading">
      <div>
        <span className="page-kicker">Working delivery examples</span>
        <h2 id="delivery-examples-heading">What a GitHub event becomes inside Provenance.</h2>
        <p>
          These examples show five customer-demo delivery moments without requiring writeback
          permissions: source event, gate projection, replay failure, coverage honesty, and risk
          drift.
        </p>
      </div>
      <div className="delivery-example-grid">
        {DELIVERY_EXAMPLES.map((example, index) => (
          <article key={example.title}>
            <span>{String(index + 1).padStart(2, "0")}</span>
            <h3>{example.title}</h3>
            <code>{example.event}</code>
            <p>{example.outcome}</p>
            <small>{example.evidence}</small>
          </article>
        ))}
      </div>
    </section>
  );
}

function CheckRunPanel({ scans }: { scans: ScanView[] }) {
  const scanFieldId = useId();
  const headShaFieldId = useId();
  const [scanId, setScanId] = useState("");
  const [headSha, setHeadSha] = useState("");
  const [result, setResult] = useState<CheckRunProjection | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!scanId || !headSha.trim()) return;
    setLoading(true);
    setError(null);
    try {
      setResult(await api.getCheckRun(scanId, headSha.trim()));
    } catch (reason) {
      setResult(null);
      setError(
        reason instanceof ApiError
          ? reason.message
          : "The check-run projection could not be computed.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!scanId && scans[0]?.id) setScanId(scans[0].id);
  }, [scanId, scans]);

  return (
    <section className="page-section" aria-labelledby="check-run-heading">
      <h2 id="check-run-heading">Check-run projection</h2>
      <p>
        Computes the source-control check outcome for one immutable scan and records the decision to
        the append-only audit trail. The head commit SHA is supplied explicitly so the decision
        stays tied to one reviewed source state.
      </p>
      <form className="delivery-form" onSubmit={(event) => void submit(event)}>
        <label className="form-field" htmlFor={scanFieldId}>
          <span>Scan</span>
          <select
            id={scanFieldId}
            required
            value={scanId}
            onChange={(event) => setScanId(event.target.value)}
          >
            <option value="">Select a scan…</option>
            {scans.map((scan) => (
              <ScanOption scan={scan} key={scan.id} />
            ))}
          </select>
        </label>
        <label className="form-field" htmlFor={headShaFieldId}>
          <span>Head commit SHA</span>
          <input
            id={headShaFieldId}
            required
            maxLength={128}
            value={headSha}
            placeholder="e.g. from a stored webhook delivery below"
            spellCheck={false}
            onChange={(event) => setHeadSha(event.target.value)}
          />
        </label>
        <button className="button button--primary" type="submit" disabled={loading}>
          <PlayCircle aria-hidden="true" size={15} />
          {loading ? "Computing" : "Compute check run"}
        </button>
      </form>
      {error ? (
        <div className="inline-alert inline-alert--error" role="alert">
          <AlertTriangle aria-hidden="true" size={16} />
          <span>{error}</span>
        </div>
      ) : null}
      {result ? (
        <div className="check-run-result">
          <Badge
            label={titleCase(result.checkRun.conclusion)}
            tone={CHECK_RUN_TONE[result.checkRun.conclusion]}
          />
          <dl className="detail-grid">
            <div>
              <dt>Findings evaluated</dt>
              <dd>{result.findingCount}</dd>
            </div>
            <div>
              <dt>Coverage</dt>
              <dd className={result.coverageComplete ? undefined : "text-warning"}>
                {result.coverageComplete ? "Complete" : "Incomplete"}
              </dd>
            </div>
          </dl>
          <p>{result.checkRun.summary}</p>
        </div>
      ) : null}
    </section>
  );
}

function ComparisonPanel({ scans }: { scans: ScanView[] }) {
  const baseFieldId = useId();
  const headFieldId = useId();
  const [baseScanId, setBaseScanId] = useState("");
  const [headScanId, setHeadScanId] = useState("");
  const [result, setResult] = useState<ComparisonResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!baseScanId || !headScanId) return;
    setLoading(true);
    setError(null);
    try {
      setResult(await api.getComparison(baseScanId, headScanId));
    } catch (reason) {
      setResult(null);
      setError(
        reason instanceof ApiError ? reason.message : "The comparison could not be computed.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!baseScanId && scans[0]?.id) setBaseScanId(scans[0].id);
    if (!headScanId && scans[1]?.id) setHeadScanId(scans[1].id);
  }, [baseScanId, headScanId, scans]);

  return (
    <section className="page-section" aria-labelledby="comparison-heading">
      <h2 id="comparison-heading">Differential comparison</h2>
      <p>
        Classifies the finding-set difference between two scans of the same repository as new,
        persistent, fixed, or reopened. A full immutable scan remains the truth source; this
        comparison never rewrites either scan's findings.
      </p>
      <form className="delivery-form" onSubmit={(event) => void submit(event)}>
        <label className="form-field" htmlFor={baseFieldId}>
          <span>Base scan</span>
          <select
            id={baseFieldId}
            required
            value={baseScanId}
            onChange={(event) => setBaseScanId(event.target.value)}
          >
            <option value="">Select a scan…</option>
            {scans.map((scan) => (
              <ScanOption scan={scan} key={scan.id} />
            ))}
          </select>
        </label>
        <label className="form-field" htmlFor={headFieldId}>
          <span>Head scan</span>
          <select
            id={headFieldId}
            required
            value={headScanId}
            onChange={(event) => setHeadScanId(event.target.value)}
          >
            <option value="">Select a scan…</option>
            {scans.map((scan) => (
              <ScanOption scan={scan} key={scan.id} />
            ))}
          </select>
        </label>
        <button className="button button--primary" type="submit" disabled={loading}>
          <GitCompareArrows aria-hidden="true" size={15} />
          {loading ? "Comparing" : "Compare"}
        </button>
      </form>
      {error ? (
        <div className="inline-alert inline-alert--error" role="alert">
          <AlertTriangle aria-hidden="true" size={16} />
          <span>{error}</span>
        </div>
      ) : null}
      {result && !result.comparable ? (
        <div className="inline-alert" role="status">
          <Info aria-hidden="true" size={16} />
          <span>{result.reason}</span>
        </div>
      ) : null}
      {result?.comparable ? (
        result.rows.length === 0 ? (
          <EmptyState
            title="No findings on either scan"
            description="There is nothing to compare."
          />
        ) : (
          <section className="table-scroll" aria-label="Comparison results table">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Finding</th>
                  <th>Outcome</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {result.rows.map((row) => (
                  <tr className="data-row" key={row.stableKey}>
                    <td>
                      <code>{row.stableKey}</code>
                    </td>
                    <td>
                      <Badge label={titleCase(row.outcome)} tone={COMPARISON_TONE[row.outcome]} />
                    </td>
                    <td>{row.reason ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )
      ) : null}
    </section>
  );
}

export function DeliveryPage() {
  const loadDeliveries = useCallback((signal: AbortSignal) => api.getGithubDeliveries(signal), []);
  const deliveries = useApiResource(loadDeliveries, 15_000);
  const loadScans = useCallback(
    (signal: AbortSignal) => api.getScans({ page: 1, pageSize: 100 }, signal),
    [],
  );
  const scans = useApiResource(loadScans, 15_000);
  const items = deliveries.data?.items ?? [];
  const scanItems = scans.data?.items ?? [];
  const counts = useMemo(() => countDeliveryStatuses(items), [items]);

  return (
    <section className="page" aria-labelledby="delivery-heading">
      <div className="page-heading">
        <div>
          <span className="page-kicker">Read-only gate evidence</span>
          <h1 id="delivery-heading">GitHub delivery</h1>
          <p>
            Signed source-control receipts, check projections, and risk drift. Hosted PoC delivery
            does not write back to GitHub.
          </p>
        </div>
        <div className="page-heading__actions">
          <button
            className="icon-button"
            type="button"
            aria-label="Refresh GitHub delivery data"
            title="Refresh GitHub delivery data"
            disabled={deliveries.isRefreshing || scans.isRefreshing}
            onClick={() => {
              deliveries.refresh();
              scans.refresh();
            }}
          >
            <RefreshCw
              aria-hidden="true"
              className={deliveries.isRefreshing || scans.isRefreshing ? "spin" : undefined}
              size={17}
            />
          </button>
        </div>
      </div>

      <div className="inline-alert" role="status">
        <Info aria-hidden="true" size={16} />
        <span>
          Hosted PoC boundary: delivery evidence is locally signed and read-only. It proves the
          intake, comparison, and audit workflow without granting repository writeback.
        </span>
      </div>

      <section className="evidence-decision-hero" aria-label="Delivery receipt summary">
        <div>
          <span className="page-kicker">Stored receipts</span>
          <h2>What did source control actually send?</h2>
          <p>{deliveryHeadline(counts)}</p>
        </div>
        <dl className="evidence-hero-metrics">
          <div data-metric="processed">
            <dt>Processed</dt>
            <dd>{counts.processed}</dd>
          </div>
          <div data-metric="failed">
            <dt>Failed</dt>
            <dd>{counts.failed}</dd>
          </div>
          <div data-metric="received">
            <dt>Received</dt>
            <dd>{counts.received}</dd>
          </div>
        </dl>
      </section>

      <section className="page-section" aria-labelledby="deliveries-heading">
        <h2 id="deliveries-heading">Stored webhook deliveries</h2>
        {deliveries.error && deliveries.data ? (
          <InlineError error={deliveries.error} onRetry={deliveries.refresh} />
        ) : null}
        {deliveries.isLoading && !deliveries.data ? <LoadingTable columns={7} rows={5} /> : null}
        {deliveries.error && !deliveries.data ? (
          <InlineError error={deliveries.error} onRetry={deliveries.refresh} />
        ) : null}
        {!deliveries.isLoading && deliveries.data && items.length === 0 ? (
          <EmptyState
            title="No webhook deliveries stored yet"
            description="Empty is not a green merge gate. Receipts appear once a signed source-control event is admitted under the PoC authorization boundary."
          />
        ) : null}
        {items.length > 0 ? (
          <section className="delivery-queue" aria-label="Webhook delivery receipts">
            {items.map((delivery) => (
              <article
                className="delivery-queue-row"
                data-status={delivery.status}
                key={delivery.id}
              >
                <div>
                  <strong>
                    <GitPullRequest aria-hidden="true" size={14} /> {delivery.eventType}
                    {delivery.action ? ` · ${delivery.action}` : ""}
                  </strong>
                  <small>{delivery.repositoryFullName}</small>
                </div>
                <code title={delivery.headSha ?? undefined}>
                  {delivery.headSha ? shortHash(delivery.headSha, 12) : "no head SHA"}
                </code>
                <Badge
                  label={titleCase(delivery.status)}
                  tone={deliveryStatusTone(delivery.status)}
                />
                <time dateTime={delivery.receivedAt}>{formatDateTime(delivery.receivedAt)}</time>
              </article>
            ))}
          </section>
        ) : null}
      </section>

      <details className="governance-detail">
        <summary>Project a check run or compare two scans</summary>
        <CheckRunPanel scans={scanItems} />
        <ComparisonPanel scans={scanItems} />
      </details>

      <details className="governance-detail">
        <summary>Example GitHub event lifecycle, not these receipts</summary>
        <DeliveryExamples />
        <section className="delivery-journey" aria-label="Delivery workflow">
          <article>
            <span>01</span>
            <h2>Admit source event</h2>
            <p>Bind a signed source-control event to one immutable scan record.</p>
          </article>
          <article>
            <span>02</span>
            <h2>Project the gate</h2>
            <p>Turn replayed findings and coverage into a check outcome reviewers can trust.</p>
          </article>
          <article>
            <span>03</span>
            <h2>Compare risk drift</h2>
            <p>Separate new, persistent, fixed, and reopened findings without rewriting history.</p>
          </article>
        </section>
      </details>
    </section>
  );
}
